type RetryableImageStatus = "idle" | "loading" | "loaded" | "failed";

export interface RetryableImageController {
  onConnectivityOrVisibilityChange: () => void;
  getStatus: () => RetryableImageStatus;
}

const controllers = new Map<string, RetryableImageController>();

let isInitialized = false;
let cleanupListeners: (() => void) | null = null;

const NOTIFY_STAGGER_MS = 50;

function isDocumentVisible(): boolean {
  // In some webviews, `document` can be present but visibilityState can be undefined.
  if (typeof document === "undefined") return true;
  if (!("visibilityState" in document)) return true;
  return document.visibilityState === "visible";
}

function isOnline(): boolean {
  // navigator.onLine is imperfect, but works as a cheap signal.
  if (typeof navigator === "undefined") return true;
  if (!("onLine" in navigator)) return true;
  return navigator.onLine;
}

function notifyAllControllers(): void {
  if (!isDocumentVisible() || !isOnline()) return;

  // Stagger notifications to avoid a thundering herd of retries when many images are visible.
  // This is especially relevant on iOS/WKWebView where backgrounding can "stall" many requests.
  let delay = 0;
  controllers.forEach((controller) => {
    setTimeout(() => {
      if (!isDocumentVisible() || !isOnline()) return;
      try {
        controller.onConnectivityOrVisibilityChange();
      } catch {
        // Never let a single controller break retries for others.
      }
    }, delay);
    delay += NOTIFY_STAGGER_MS;
  });
}

function ensureInitialized(): void {
  if (isInitialized) return;
  isInitialized = true;

  // When iOS/WKWebView backgrounds, in-flight image requests can be dropped without firing
  // load/error. On returning to the foreground, re-kick pending images.
  if (typeof document !== "undefined") {
    const onVisibilityChange = () => {
      if (isDocumentVisible()) {
        // Let the browser settle first.
        setTimeout(() => notifyAllControllers(), 150);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    cleanupListeners = () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }

  if (typeof window !== "undefined") {
    const onOnline = () => notifyAllControllers();
    const onFocus = () => notifyAllControllers();
    const onPageShow = () => notifyAllControllers();

    window.addEventListener("online", onOnline);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);

    const previousCleanup = cleanupListeners;
    cleanupListeners = () => {
      previousCleanup?.();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
  }
}

export function registerRetryableImageController(
  id: string,
  controller: RetryableImageController,
): () => void {
  ensureInitialized();
  controllers.set(id, controller);

  return () => {
    controllers.delete(id);
  };
}

/**
 * Optional cleanup hook for test/SSR environments.
 * In the app runtime this registry is a process-long singleton.
 */
export function cleanupRetryableImageRegistry(): void {
  cleanupListeners?.();
  cleanupListeners = null;
  controllers.clear();
  isInitialized = false;
}

